import { useState, type InputHTMLAttributes } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import styles from "./FormInputs.module.css";

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  id: string;
  label: string;
  error?: string;
}

export function PasswordInput({
  id,
  label,
  error,
  className,
  disabled,
  ...rest
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const errorId = error ? `${id}-error` : undefined;
  const toggleId = `${id}-toggle`;

  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div
        className={[
          styles.control,
          error ? styles.controlError : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span className={styles.leadingIcon} aria-hidden>
          <Lock size={18} strokeWidth={1.75} />
        </span>
        <input
          id={id}
          type={visible ? "text" : "password"}
          className={styles.input}
          aria-label={label}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          autoComplete="current-password"
          disabled={disabled}
          {...rest}
        />
        <button
          id={toggleId}
          type="button"
          className={styles.toggle}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "비밀번호 숨기기" : "비밀번호 보기"}
          aria-pressed={visible}
          disabled={disabled}
          tabIndex={0}
        >
          {visible ? (
            <EyeOff size={18} strokeWidth={1.75} aria-hidden />
          ) : (
            <Eye size={18} strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </div>
      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
